import type { CrowdexAssistant } from '@crowdex/agent-contracts/api';
import type { ViewOrigin } from '@crowdex/plugin-contracts';
import { useCallback, useMemo } from 'react';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { useWorkspaceDirectory, workspaceDirectoryStore } from '@/lib/assistants/directory';
import { showConversation } from './app-conversation';
import type { FrameOrigin } from './plugin-frame';

/**
 * Who made an item (the extensions proposal's "Origine"): the assistant of the conversation that
 * made it, which the host draws as "From <assistant>" and hands its view as `context.origin`.
 */
export type ItemOrigin = { assistant: CrowdexAssistant; conversation: string };

/** The assistant a conversation is under (its channel's too), from the directory's state. */
export function assistantOf(
  state: {
    projects: readonly CrowdexAssistant[];
    placements: Record<string, { projectId: string | null } | undefined>;
  },
  conversation: string | null,
): CrowdexAssistant | null {
  if (conversation === null) return null;
  const id =
    state.placements[conversation]?.projectId ??
    state.projects.find((project) => project.conversationId === conversation)?.id ??
    null;
  return id === null ? null : (state.projects.find((project) => project.id === id) ?? null);
}

/** Reads the origin of the items made in a conversation, kept current with the assistants. */
export function useItemOrigin(): (conversation: string | null) => ItemOrigin | null {
  const projects = useWorkspaceDirectory((state) => state.projects);
  const placements = useWorkspaceDirectory((state) => state.placements);
  return useCallback(
    (conversation) => {
      const assistant = assistantOf({ projects, placements }, conversation);
      return assistant === null || conversation === null ? null : { assistant, conversation };
    },
    [projects, placements],
  );
}

/** The origin as the view reads it (`context.origin`). */
export function viewOrigin(origin: ItemOrigin | null): ViewOrigin | null {
  if (origin === null) return null;
  return {
    assistant: {
      id: origin.assistant.id,
      name: origin.assistant.name,
      color: origin.assistant.color,
    },
    conversation: origin.conversation,
  };
}

/**
 * The assistant an avatar serves (docs/adr-assistants.md: one at most), for an item of a call
 * plugin (an avatar of its library), as its view reads it (`context.origin`); undefined for an
 * item of another plugin, null for an avatar no assistant has.
 */
export function avatarOrigin(
  projects: readonly CrowdexAssistant[],
  avatar: string,
): ViewOrigin | null {
  const assistant = projects.find(
    (project) => project.avatarId === avatar && project.archivedAt === null,
  );
  if (assistant === undefined) return null;
  return {
    assistant: { id: assistant.id, name: assistant.name, color: assistant.color },
    ...(assistant.conversationId ? { conversation: assistant.conversationId } : {}),
  };
}

/** `avatarOrigin` of the item a plugin's view shows, kept current with the assistants. */
export function useAvatarOrigin(
  plugin: string,
  item: string | null,
): ViewOrigin | null | undefined {
  const callPlugin = useWorkbench().callViews.some((view) => view.plugin === plugin);
  const projects = useWorkspaceDirectory((state) => state.projects);
  return useMemo(
    () => (!callPlugin || item === null ? undefined : avatarOrigin(projects, item)),
    [callPlugin, item, projects],
  );
}

/** Opens an assistant's channel (made the first time), from a page outside a conversation. */
export async function openAssistantChannel(
  assistant: Pick<CrowdexAssistant, 'id' | 'conversationId'>,
): Promise<boolean> {
  const channel =
    assistant.conversationId ??
    (await workspaceDirectoryStore().getState().openAssistant(assistant.id));
  return showConversation(channel);
}

/** The origin as the frame draws it: "From <assistant>", a click opening its channel. */
export function frameOrigin(origin: ItemOrigin | null): FrameOrigin | null {
  if (origin === null) return null;
  return {
    name: origin.assistant.name,
    color: origin.assistant.color,
    onOpen: () => void openAssistantChannel(origin.assistant),
  };
}
